/**
 * The file name a response's `Content-Disposition` gives, preferring the RFC 5987 `filename*` form ASP.NET Core
 * writes beside the plain one.
 */
const fileNameOf = (response: Response): string | null => {
  const disposition = response.headers.get("Content-Disposition");
  if (!disposition) return null;

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // Fall through to the plain form.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1] : null;
};

/** Hands a blob to the browser as a download. */
export const saveBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next tick: revoking straight away can cancel the download before the browser has started it.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/** Saves a successful response's body as a download, named as the server named it. */
export const saveResponseAsFile = async (response: Response, fallbackName: string): Promise<void> =>
  saveBlob(await response.blob(), fileNameOf(response) ?? fallbackName);
